import Grid from "@mui/material/Grid"
import React, { useMemo, useState } from "react"
import { Box, FormControl } from "@mui/material"
import {
  useGetRunsNodesResultsHistoricAggregateQuery,
  useGetRunsNodesResultsHistoricQuery,
} from "@store/services/runService"
import { TimeSeriesContainer } from "./TimeSeriesContainer"
import { ProbabilityDensityContainer } from "./ProbabilityDensityContainer"
import { RunDto } from "@custom-types/modelRun"
import { ScatterPlot } from "./ScatterPlot"
import { useLocation } from "react-router"
import { ScatterRawResultsPlot } from "./ScatterRawResultsPlot"
import RawResultsTable from "@features/results/Result/RawResultsTable"
import { GraphDataViewModel, GroupNodeLabel } from "@features/graph/graphViewModel"
import {
  graphSampleNodeToViewGroupNodeLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import NodeSelect from "@components/NodeSelect"
import { GraphNodeType } from "@custom-types/graphs"
import { checkDataType } from "@features/results/Result/const"
import { BarStringsChart } from "@features/results/Result/ChartsContainer/BarStringsChart"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { calculateLagShift } from "@utils/run"
import AggregateFunctionChartContainer from "@features/results/Result/AggregateFunctionChart/AggregateFunctionChartContainer"

type ChartsContainerProps = {
  selectedNode: string
  runs: RunDto[]
}

export function ChartsContainer({ selectedNode, runs }: ChartsContainerProps) {
  const [nodeLabel, setNodeLabel] = useState<GroupNodeLabel>(
    graphSampleNodeToViewGroupNodeLabel(selectedNode),
  )
  const navigation = useLocation()
  const isRawResultsPage = navigation.pathname.includes("Results/Result")
  const runIds = runs.map((run) => run.key)
  const isMultiRunMode = runIds.length > 1

  const node = GraphDataViewModel.data?.nodeMap[nodeLabel?.key ?? ""]
  const isScenarioNode = node ? Boolean(node.display["Scenario Mnem"]) : false
  const isForecastNode = node ? Boolean(node.display["Forecast Mnem"]) : false

  const runKeys = runs.map((run) => run.key)
  const { isFetching: isFetchingHistoricData, data: historicResponse } =
    useGetRunsNodesResultsHistoricQuery({
      runKeys,
      nodes: [graphViewGroupNodeLabelToSampleNode(nodeLabel.label)],
    })
  const { isFetching: isFetchingHistoricAggregateData, data: aggregateData } =
    useGetRunsNodesResultsHistoricAggregateQuery({
      runKeys,
      nodes: [graphViewGroupNodeLabelToSampleNode(nodeLabel.label)],
    })
  const isFetching = isFetchingHistoricData || isFetchingHistoricAggregateData
  const runResults = convertHistoricToRunResults(historicResponse?.results)
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  const dataType = checkDataType(Object.values(Object.values(runResults ?? {})[0] ?? {})[0])

  const nodeOptions: GroupNodeLabel[] = structuredClone(GraphDataViewModel.groupLabels).map(
    (item) => ({
      ...item,
      format: GraphDataViewModel.data?.nodeMap[item.key]?.display?.["Unit Format"] || "",
    }),
  )

  const disabledNodeOptions = GraphDataViewModel.groupLabels
    .filter(({ type }) => type === GraphNodeType.TABLE)
    .map(({ label }) => ({
      label,
      reason: "Table nodes are not supported in charts view",
    }))

  return (
    <Grid container spacing={1} sx={{ my: 1 }}>
      <Grid item xs={3}>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <NodeSelect
            graph={GraphDataViewModel.data}
            value={nodeLabel}
            options={nodeOptions}
            getOptionDisabled={disabledNodeOptions}
            onChange={(newValue) => {
              if (!newValue) return
              newValue.format =
                GraphDataViewModel.data?.nodeMap[newValue?.key]?.display?.["Unit Format"] || ""
              setNodeLabel(newValue)
              if (!isMultiRunMode) {
                window.location.hash = encodeURIComponent(newValue.label)
              }
            }}
          />
        </FormControl>
      </Grid>
      <Grid item xs={9} />
      {dataType === "string" ? (
        <>
          <BarStringsChart
            runs={runs}
            aggregateData={aggregateData}
            nodes={nodeLabel ? [nodeLabel] : null}
            lagShift={lagShift}
          />
          <Grid item xs={12}>
            <RawResultsTable
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              runId={runIds[0]}
              node={nodeLabel}
              lagShift={lagShift}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12}>
            <TimeSeriesContainer
              runs={runs}
              isFetchingGetRunFilterNode={isFetching}
              aggregateData={aggregateData}
              historicNodes={historicResponse?.results}
              lagShift={lagShift}
              nodes={nodeLabel ? [nodeLabel] : null}
              localStoragePrefix={isRawResultsPage ? "rawResult" : "comparison"}
              isMultipleNodesMode={false}
            />
          </Grid>
          <Grid item xs={12}>
            <AggregateFunctionChartContainer
              runs={runs}
              node={nodeLabel}
              isDownloadChartImageButtonDisabled={isFetching}
            />
          </Grid>
          {!isScenarioNode && !isForecastNode && (
            <Grid item xs={12}>
              <ProbabilityDensityContainer
                runs={runs}
                isFetchingGetRunFilterNode={isFetching}
                rawResultDataFilterNodes={runResults}
                lagShift={lagShift}
                selectedNode={nodeLabel}
                localStoragePrefix={isRawResultsPage ? "rawResult" : "comparison"}
              />
            </Grid>
          )}
          {!isScenarioNode && !isForecastNode && (
            <Grid item xs={12}>
              {isRawResultsPage ? (
                <Box>
                  <ScatterRawResultsPlot
                    rawResultDataFilterNodes={runResults ?? {}}
                    lagShift={lagShift}
                    node={nodeLabel}
                    chartContainerSx={{ height: 500, mb: 3 }}
                  />
                  <RawResultsTable
                    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                    runId={runIds[0]}
                    node={nodeLabel}
                    lagShift={lagShift}
                  />
                </Box>
              ) : (
                <Box>
                  <ScatterPlot
                    runs={runs}
                    rawResultDataFilterNodes={runResults ?? {}}
                    lagShift={lagShift}
                    node={nodeLabel}
                  />
                </Box>
              )}
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}
